/**
 * @description Admin sidebar navigation component
 *
 * Key features:
 * - Top-level nav: Channel Partners, Sales Orgs, Settings, Audit Logs
 * - Integrations section with expandable sub-navigation
 * - Active route highlighting
 * - User info + sign out at bottom
 *
 * @dependencies
 * - react-router-dom: Link for CP / Sales Orgs SoT; NavLink for other items
 * - adminNavActive: visual active + aria-current for Channel Partners / Sales Orgs
 * - lucide-react: Navigation icons
 * - AuthContext: User info and sign out
 *
 * @notes
 * - Fixed position, dark slate-900 background
 * - 64px wide (w-64) matches common admin sidebar width
 * - ARIA attributes on expandable section for screen reader accessibility
 * - Sales Orgs is the /tenants list label only; routes and APIs stay /tenants
 * - Under-CP `/tenants/{id}` keeps Channel Partners selected (channelPartnerId)
 * - Default landing is Channel Partners until a dashboard exists
 */

import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAdminNav } from '../../contexts/AdminNavContext';
import {
  adminNavAriaCurrent,
  isAdminNavItemActive,
  usesAdminNavItemSoT
} from '../../utils/adminNavActive';
import {
  Building2,
  Handshake,
  Puzzle,
  Settings,
  Shield,
  ScrollText,
  LogOut,
  ChevronDown,
  CreditCard,
  Mail,
} from 'lucide-react';
import { useUser, useAuth } from '../../contexts/AuthContext';

export const navItems = [
  {
    label: 'Channel Partners',
    path: '/channel-partners',
    icon: Handshake,
  },
  {
    label: 'Sales Orgs',
    path: '/tenants',
    icon: Building2,
  },
  {
    label: 'Settings',
    path: '/settings',
    icon: Settings,
  },
  {
    label: 'Audit Logs',
    path: '/audit-logs',
    icon: ScrollText,
  },
];

const integrationsSubItems = [
  { label: 'Gateways', path: '/integrations/gateways', icon: CreditCard },
  { label: 'Email', path: '/integrations/email', icon: Mail },
];

function AdminSidebar() {
  const location = useLocation();
  const { tenantChannelPartnerId } = useAdminNav();
  const { user } = useUser();
  const { signOut } = useAuth();
  const [integrationsOpen, setIntegrationsOpen] = useState(false);

  useEffect(() => {
    if (location.pathname.startsWith('/integrations')) {
      setIntegrationsOpen(true);
    }
  }, [location.pathname]);

  const linkClasses = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
      isActive
        ? 'bg-slate-800 text-white'
        : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
    }`;

  const subLinkClasses = ({ isActive }) =>
    `flex items-center gap-3 pl-10 pr-3 py-2 rounded-lg text-sm transition-colors ${
      isActive
        ? 'bg-slate-800 text-white font-medium'
        : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
    }`;

  return (
    <aside className="fixed inset-y-0 left-0 w-64 bg-slate-900 border-r border-slate-800 flex flex-col z-30">
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 h-16 border-b border-slate-800">
        <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
          <Shield className="w-4 h-4 text-white" />
        </div>
        <span className="text-white font-semibold text-lg tracking-tight">Lynx Admin</span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" aria-label="Main navigation">
        {navItems.map((item) => {
          if (usesAdminNavItemSoT(item.path)) {
            const isActive = isAdminNavItemActive(
              item.path,
              location.pathname,
              tenantChannelPartnerId
            );
            return (
              <Link
                key={item.path}
                to={item.path}
                className={linkClasses({ isActive })}
                aria-current={adminNavAriaCurrent(isActive)}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {item.label}
              </Link>
            );
          }
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={linkClasses}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {item.label}
            </NavLink>
          );
        })}

        {/* Integrations section with sub-nav */}
        <div>
          <button
            onClick={() => setIntegrationsOpen(!integrationsOpen)}
            aria-expanded={integrationsOpen}
            aria-controls="integrations-subnav"
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              location.pathname.startsWith('/integrations')
                ? 'text-white'
                : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
            }`}
          >
            <Puzzle className="w-5 h-5 flex-shrink-0" />
            Integrations
            <ChevronDown
              className={`w-4 h-4 ml-auto transition-transform ${
                integrationsOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {integrationsOpen && (
            <div id="integrations-subnav" className="mt-1 space-y-0.5">
              {integrationsSubItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={subLinkClasses}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  {item.label}
                </NavLink>
              ))}
            </div>
          )}
        </div>

      </nav>

      {/* User info + sign out */}
      <div className="px-3 py-4 border-t border-slate-800">
        {user && (
          <div className="px-3 mb-3">
            <p className="text-sm font-medium text-white truncate">
              {user.firstName} {user.lastName}
            </p>
            <p className="text-xs text-slate-500 truncate">{user.email}</p>
          </div>
        )}
        <button
          onClick={signOut}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-slate-800/50 transition-colors"
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          Sign out
        </button>
      </div>
    </aside>
  );
}

export default AdminSidebar;
